/**
 * EducationTab — full Education content management for the Famēlia admin dashboard.
 *
 * Data is fetched from /api/admin/education/* (famelia-admin-api-web Express app).
 * NO direct Supabase client — all DB access is server-side via the API.
 *
 * Three sub-sections:
 *   1. Articles  — create / edit / publish / archive with source management + rights validation
 *   2. Categories — manage the category list shown in the mobile feed
 *   3. Trusted Sources — view seeded sources, add custom ones
 */

import React, { useCallback, useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, FormControl, FormControlLabel,
  IconButton, InputLabel, MenuItem, Paper, Select, Switch, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, Tab,
  TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PublishIcon from '@mui/icons-material/Publish';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnpublishedIcon from '@mui/icons-material/Unpublished';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LinkIcon from '@mui/icons-material/Link';
import RssFeedIcon from '@mui/icons-material/RssFeed';
import InfoIcon from '@mui/icons-material/Info';
import VerifiedIcon from '@mui/icons-material/Verified';
import { adminApi } from '../services/adminApi';

// ── API base ──────────────────────────────────────────────────────────────────
const EDU_API = `${process.env.REACT_APP_EXPRESS_API_URL || 'https://famelia-admin-api-web.azurewebsites.net'}/api/admin/education`;

// ── Types ─────────────────────────────────────────────────────────────────────
type ArticleStatus = 'DRAFT' | 'SCHEDULED' | 'PUBLISHED' | 'ARCHIVED';
type ContentOrigin = 'FAMELIA_ORIGINAL' | 'PUBLIC_DOMAIN_REUSED' | 'LICENSED' | 'EXTERNAL_LINK_ONLY';
type UsagePolicy = 'PUBLIC_DOMAIN_ALLOWED' | 'PUBLIC_DOMAIN_VERIFY_PAGE' | 'REFERENCE_LINK_ONLY' | 'LICENSED_CONTENT' | 'FAMELIA_ORIGINAL';

interface Category { id: string; name: string; slug: string; icon?: string; sort_order: number; active: boolean; }
interface TrustedSource {
  id: string; name: string; short_name: string; source_type?: string;
  usage_policy: UsagePolicy; attribution_text?: string;
  requires_page_level_rights_check: boolean; logo_use_allowed: boolean;
  notes?: string; active: boolean; is_custom: boolean;
}
interface ArticleSource {
  id?: string; trusted_source_id: string; source_title: string;
  source_url: string; source_date: string; accessed_at: string;
  is_primary: boolean; rights_verified: boolean; license_notes: string;
  trusted_source?: TrustedSource;
}
interface Article {
  id?: string; title: string; short_description: string; body: string;
  body_format: string; category_id: string; status: ArticleStatus;
  featured: boolean; sort_priority: number; reading_time_minutes: number | '';
  content_origin: ContentOrigin; primary_source_id: string;
  reviewed_by_dietitian: boolean; reviewer_name: string;
  reviewer_credentials: string; reviewed_at: string;
  published_at: string; language: string; hero_icon: string;
  category?: Category;
}

// ── Helpers ───────────────────────────────────────────────────────────────────
const BLANK_ARTICLE: Article = {
  title: '', short_description: '', body: '', body_format: 'markdown',
  category_id: '', status: 'DRAFT', featured: false, sort_priority: 0,
  reading_time_minutes: '', content_origin: 'FAMELIA_ORIGINAL',
  primary_source_id: '', reviewed_by_dietitian: false,
  reviewer_name: '', reviewer_credentials: '', reviewed_at: '',
  published_at: '', language: 'en', hero_icon: '',
};
const BLANK_SOURCE: ArticleSource = {
  trusted_source_id: '', source_title: '', source_url: 'https://',
  source_date: '', accessed_at: '', is_primary: false,
  rights_verified: false, license_notes: '',
};

const STATUS_COLORS: Record<ArticleStatus, 'default' | 'warning' | 'success' | 'error' | 'info'> = {
  DRAFT: 'default', SCHEDULED: 'info', PUBLISHED: 'success', ARCHIVED: 'error',
};
const POLICY_META: Record<UsagePolicy, { label: string; chip: 'success' | 'warning' | 'info' | 'secondary' | 'default' }> = {
  PUBLIC_DOMAIN_ALLOWED:     { label: 'Public domain reuse may be allowed', chip: 'success' },
  PUBLIC_DOMAIN_VERIFY_PAGE: { label: 'Verify this specific page before reusing', chip: 'warning' },
  REFERENCE_LINK_ONLY:       { label: 'Reference / link only', chip: 'info' },
  LICENSED_CONTENT:          { label: 'Licensed content', chip: 'secondary' },
  FAMELIA_ORIGINAL:          { label: 'Famēlia original', chip: 'success' },
};
const policyBadge = (policy: UsagePolicy) => {
  const m = POLICY_META[policy];
  return <Chip label={m.label} size="small" color={m.chip} variant="outlined" sx={{ fontWeight: 700, fontSize: 11 }} />;
};

// ── Main component ─────────────────────────────────────────────────────────────
export function EducationTab() {
  const [section, setSection] = useState(0);
  return (
    <Box>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3, pt: 2 }}>
        <Typography variant="h5" fontWeight="bold" sx={{ mb: 1 }}>Education</Typography>
        <Tabs value={section} onChange={(_, v) => setSection(v)}>
          <Tab label="📰 Articles" />
          <Tab label="🏷️ Categories" />
          <Tab label="🔒 Trusted Sources" />
          <Tab label="📬 User Submissions" />
        </Tabs>
      </Box>
      {section === 0 && <ArticlesSection />}
      {section === 1 && <CategoriesSection />}
      {section === 2 && <TrustedSourcesSection />}
      {section === 3 && <UserSubmissionsSection />}
    </Box>
  );
}

// ── Articles section ──────────────────────────────────────────────────────────
function ArticlesSection() {
  const authHeader = useMemo(() => ({ Authorization: adminApi.getAuthToken() }), []);
  const [articles, setArticles] = useState<Article[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [sources, setSources] = useState<TrustedSource[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<ArticleStatus | 'ALL'>('ALL');
  const [editing, setEditing] = useState<Article | null>(null);
  const [editingSources, setEditingSources] = useState<ArticleSource[]>([]);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewArticle, setPreviewArticle] = useState<Article | null>(null);

  // ── URL import state ──
  const [importOpen, setImportOpen] = useState(false);
  const [importUrl, setImportUrl] = useState('');
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [artsRes, catsRes, srcsRes] = await Promise.all([
        axios.get(`${EDU_API}/articles`, { headers: authHeader }),
        axios.get(`${EDU_API}/categories`, { headers: authHeader }),
        axios.get(`${EDU_API}/trusted-sources`, { headers: authHeader }),
      ]);
      setArticles(artsRes.data.articles ?? []);
      setCategories(catsRes.data.categories ?? []);
      setSources(srcsRes.data.sources ?? []);
    } catch (e: any) {
      setError(e.response?.data?.error ?? e.message);
    }
    setLoading(false);
  }, [authHeader]);

  useEffect(() => { load(); }, [load]);

  const openEdit = async (art: Article) => {
    try {
      const { data } = await axios.get(`${EDU_API}/articles/${art.id}`, { headers: authHeader });
      const resolvedSrcs: ArticleSource[] = (data.sources ?? []).map((s: any) => ({
        id: s.id,
        trusted_source_id: s.trusted_source_id,
        source_title: s.source_title ?? '',
        source_url: s.source_url ?? 'https://',
        source_date: s.source_date ?? '',
        accessed_at: s.accessed_at ?? '',
        is_primary: s.is_primary,
        rights_verified: s.rights_verified,
        license_notes: s.license_notes ?? '',
        trusted_source: s.trusted_source,
      }));
      setEditing({ ...data.article });
      setEditingSources(resolvedSrcs);
    } catch { setError('Could not load article details.'); }
  };

  const openNew = () => { setEditing({ ...BLANK_ARTICLE }); setEditingSources([]); };

  const validateForPublish = (art: Article, artSrcs: ArticleSource[]): string | null => {
    if (!art.title.trim()) return 'Headline is required.';
    if (!art.category_id) return 'Category is required.';
    if (!art.short_description.trim()) return 'Short description is required.';
    for (const s of artSrcs) {
      const src = sources.find(x => x.id === s.trusted_source_id);
      if (!src) continue;
      if (src.usage_policy === 'REFERENCE_LINK_ONLY' && art.content_origin === 'PUBLIC_DOMAIN_REUSED') {
        return `"${src.short_name}" is Reference/Link Only — cannot publish as Public Domain Reused.`;
      }
      if ((src.usage_policy === 'PUBLIC_DOMAIN_VERIFY_PAGE' || src.usage_policy === 'PUBLIC_DOMAIN_ALLOWED') &&
          art.content_origin === 'PUBLIC_DOMAIN_REUSED' && !s.rights_verified) {
        return `You must verify the "${src.short_name}" page rights before publishing as Public Domain Reused.`;
      }
      if (art.content_origin === 'LICENSED' && !s.license_notes?.trim()) {
        return `Content origin is Licensed — please enter license notes for "${src.short_name}".`;
      }
    }
    return null;
  };

  const handleSave = async (targetStatus?: ArticleStatus) => {
    if (!editing) return;
    const status = targetStatus ?? editing.status;
    if (status === 'PUBLISHED') {
      const err = validateForPublish(editing, editingSources);
      if (err) { setError(err); return; }
    }
    setSaving(true);
    try {
      const payload = {
        ...editing,
        status,
        published_at: status === 'PUBLISHED' ? (editing.published_at || new Date().toISOString()) : editing.published_at || null,
        archived_at: status === 'ARCHIVED' ? new Date().toISOString() : null,
        reading_time_minutes: editing.reading_time_minutes === '' ? null : editing.reading_time_minutes,
      };

      let articleId = editing.id;
      if (articleId) {
        await axios.put(`${EDU_API}/articles/${articleId}`, payload, { headers: authHeader });
      } else {
        const { data } = await axios.post(`${EDU_API}/articles`, payload, { headers: authHeader });
        articleId = data.id;
      }

      // Upsert sources
      for (const s of editingSources) {
        const srcPayload = {
          trusted_source_id: s.trusted_source_id,
          source_title: s.source_title || null,
          source_url: s.source_url || null,
          source_date: s.source_date || null,
          accessed_at: s.accessed_at || null,
          is_primary: s.is_primary,
          rights_verified: s.rights_verified,
          license_notes: s.license_notes || null,
        };
        if (s.id) {
          await axios.put(`${EDU_API}/article-sources/${s.id}`, srcPayload, { headers: authHeader });
        } else {
          await axios.post(`${EDU_API}/articles/${articleId}/sources`, srcPayload, { headers: authHeader });
        }
      }

      setEditing(null);
      load();
    } catch (e: any) {
      setError(e.response?.data?.error ?? e.message);
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try { await axios.delete(`${EDU_API}/articles/${deleteId}`, { headers: authHeader }); }
    catch { setError('Delete failed'); }
    setDeleteId(null);
    load();
  };

  const handleImportUrl = async () => {
    if (!importUrl.trim()) return;
    setImporting(true);
    setImportError(null);
    try {
      const { data } = await axios.post(
        `${EDU_API}/import-url`,
        { url: importUrl.trim() },
        { headers: authHeader, timeout: 15000 },
      );

      // Pre-fill a new article from the scraped metadata
      const prefilled: Article = {
        ...BLANK_ARTICLE,
        title: data.title || '',
        short_description: data.short_description || '',
        content_origin: data.suggested_origin ?? 'EXTERNAL_LINK_ONLY',
        // body stays blank — editor will fill it in
      };

      // Pre-attach a source if a trusted source was matched
      const preSources: ArticleSource[] = [];
      if (data.source_url) {
        preSources.push({
          ...BLANK_SOURCE,
          source_url: data.source_url,
          source_title: data.title || '',
          trusted_source_id: data.matched_source?.id || '',
          trusted_source: data.matched_source || undefined,
          is_primary: true,
          accessed_at: new Date().toISOString().split('T')[0],
        });
      }

      setImportOpen(false);
      setImportUrl('');
      setEditing(prefilled);
      setEditingSources(preSources);
    } catch (e: any) {
      setImportError(e.response?.data?.error ?? e.message ?? 'Could not fetch that URL.');
    }
    setImporting(false);
  };

  const quickStatus = async (id: string, status: ArticleStatus) => {
    const patch: any = { status };
    if (status === 'PUBLISHED') patch.published_at = new Date().toISOString();
    if (status === 'ARCHIVED') patch.archived_at = new Date().toISOString();
    try { await axios.put(`${EDU_API}/articles/${id}`, patch, { headers: authHeader }); load(); }
    catch { setError('Status update failed'); }
  };

  const visible = filterStatus === 'ALL' ? articles : articles.filter(a => a.status === filterStatus);

  return (
    <Box sx={{ p: 3 }}>
      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        {(['ALL', 'DRAFT', 'PUBLISHED', 'SCHEDULED', 'ARCHIVED'] as const).map(s => (
          <Chip key={s} onClick={() => setFilterStatus(s)}
            label={s === 'ALL' ? `All (${articles.length})` : `${s} (${articles.filter(a => a.status === s).length})`}
            color={filterStatus === s ? (s === 'ALL' ? 'primary' : STATUS_COLORS[s as ArticleStatus] || 'default') : 'default'}
            variant={filterStatus === s ? 'filled' : 'outlined'} />
        ))}
        <Box sx={{ flexGrow: 1 }} />
        <Button variant="outlined" startIcon={<LinkIcon />} onClick={() => { setImportOpen(true); setImportError(null); setImportUrl(''); }}>
          Import from URL
        </Button>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openNew} sx={{ bgcolor: '#1565C0' }}>New Article</Button>
      </Box>

      {/* URL Import Dialog */}
      <Dialog open={importOpen} onClose={() => setImportOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Import article from URL
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Paste a link to any nutrition or health article. We'll pull the headline and description so you don't have to retype them. You can edit everything before publishing.
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {importError && <Alert severity="error" onClose={() => setImportError(null)} sx={{ mb: 2 }}>{importError}</Alert>}
          <Alert severity="info" sx={{ mb: 2, fontSize: 13 }}>
            <strong>Rights reminder:</strong> Most articles are <em>Reference / Link Only</em> — you can link to them and quote short excerpts with attribution, but you cannot copy the full article text. The form will suggest the correct content origin based on the source.
          </Alert>
          <TextField
            autoFocus fullWidth size="small"
            label="Article URL *"
            placeholder="https://www.healthline.com/nutrition/..."
            value={importUrl}
            onChange={e => setImportUrl(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleImportUrl(); }}
            disabled={importing}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportOpen(false)} disabled={importing}>Cancel</Button>
          <Button
            variant="contained" onClick={handleImportUrl}
            disabled={importing || !importUrl.trim().startsWith('http')}
            sx={{ bgcolor: '#1565C0' }}
            startIcon={importing ? <CircularProgress size={16} color="inherit" /> : <LinkIcon />}
          >
            {importing ? 'Fetching…' : 'Import'}
          </Button>
        </DialogActions>
      </Dialog>

      {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box> : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead sx={{ bgcolor: '#F5F5F5' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Headline</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Origin</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>RD?</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Published</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.length === 0 && (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No articles yet. Click "New Article" to get started.
                </TableCell></TableRow>
              )}
              {visible.map(a => (
                <TableRow key={a.id} hover>
                  <TableCell sx={{ maxWidth: 280 }}>
                    <Typography variant="body2" fontWeight={600} sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {a.featured && <span>⭐ </span>}{a.title}
                    </Typography>
                    {a.reading_time_minutes && <Typography variant="caption" color="text.secondary">{a.reading_time_minutes} min read</Typography>}
                  </TableCell>
                  <TableCell><Typography variant="body2">{(a as any).category?.name ?? '—'}</Typography></TableCell>
                  <TableCell><Chip label={a.status} size="small" color={STATUS_COLORS[a.status]} /></TableCell>
                  <TableCell><Typography variant="caption">{a.content_origin?.replace(/_/g, ' ')}</Typography></TableCell>
                  <TableCell>
                    {a.reviewed_by_dietitian ? (
                      <Tooltip title={`${a.reviewer_name ?? 'RD'} ${a.reviewer_credentials ?? ''}`}>
                        <VerifiedIcon fontSize="small" color="success" />
                      </Tooltip>
                    ) : '—'}
                  </TableCell>
                  <TableCell><Typography variant="caption">{a.published_at ? new Date(a.published_at).toLocaleDateString() : '—'}</Typography></TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                      <Tooltip title="Preview"><IconButton size="small" onClick={() => { setPreviewArticle(a); setPreviewOpen(true); }}><OpenInNewIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(a)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                      {a.status === 'DRAFT' && <Tooltip title="Publish"><IconButton size="small" color="success" onClick={() => quickStatus(a.id!, 'PUBLISHED')}><PublishIcon fontSize="small" /></IconButton></Tooltip>}
                      {a.status === 'PUBLISHED' && <Tooltip title="Unpublish"><IconButton size="small" color="warning" onClick={() => quickStatus(a.id!, 'DRAFT')}><UnpublishedIcon fontSize="small" /></IconButton></Tooltip>}
                      {a.status !== 'ARCHIVED' && <Tooltip title="Archive"><IconButton size="small" onClick={() => quickStatus(a.id!, 'ARCHIVED')}><ArchiveIcon fontSize="small" /></IconButton></Tooltip>}
                      <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setDeleteId(a.id!)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {editing && (
        <ArticleEditDialog article={editing} articleSources={editingSources} categories={categories}
          trustedSources={sources} saving={saving} onChangeArticle={setEditing}
          onChangeSources={setEditingSources} onSave={handleSave} onClose={() => setEditing(null)}
          error={error} onClearError={() => setError(null)} />
      )}

      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Article Preview</DialogTitle>
        <DialogContent dividers>{previewArticle && <ArticlePreview article={previewArticle} />}</DialogContent>
        <DialogActions><Button onClick={() => setPreviewOpen(false)}>Close</Button></DialogActions>
      </Dialog>

      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)}>
        <DialogTitle>Delete article?</DialogTitle>
        <DialogContent><Typography>This permanently removes the article. Cannot be undone.</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── Article Edit Dialog ───────────────────────────────────────────────────────
interface EditDialogProps {
  article: Article; articleSources: ArticleSource[]; categories: Category[];
  trustedSources: TrustedSource[]; saving: boolean;
  onChangeArticle: (a: Article) => void; onChangeSources: (s: ArticleSource[]) => void;
  onSave: (status?: ArticleStatus) => void; onClose: () => void;
  error: string | null; onClearError: () => void;
}

function ArticleEditDialog({ article, articleSources, categories, trustedSources, saving,
  onChangeArticle, onChangeSources, onSave, onClose, error, onClearError }: EditDialogProps) {
  const [addingSource, setAddingSource] = useState(false);
  const [newSrc, setNewSrc] = useState<ArticleSource>({ ...BLANK_SOURCE });

  const set = (patch: Partial<Article>) => onChangeArticle({ ...article, ...patch });
  const resolveSrc = (id: string) => trustedSources.find(s => s.id === id);
  const hasNCM = articleSources.some(s => resolveSrc(s.trusted_source_id)?.short_name === 'Nutrition Care Manual');

  const addSource = () => {
    if (!newSrc.trusted_source_id) return;
    onChangeSources([...articleSources, { ...newSrc, trusted_source: resolveSrc(newSrc.trusted_source_id) }]);
    setNewSrc({ ...BLANK_SOURCE });
    setAddingSource(false);
  };
  const removeSource = (idx: number) => onChangeSources(articleSources.filter((_, i) => i !== idx));
  const updateSource = (idx: number, patch: Partial<ArticleSource>) =>
    onChangeSources(articleSources.map((s, i) => i === idx ? { ...s, ...patch } : s));

  const needsVerify = (s: ArticleSource) => {
    const src = resolveSrc(s.trusted_source_id);
    return src && (src.usage_policy === 'PUBLIC_DOMAIN_VERIFY_PAGE' || src.usage_policy === 'PUBLIC_DOMAIN_ALLOWED')
      && article.content_origin === 'PUBLIC_DOMAIN_REUSED';
  };
  const isBlocked = (srcId: string) =>
    resolveSrc(srcId)?.usage_policy === 'REFERENCE_LINK_ONLY' && article.content_origin === 'PUBLIC_DOMAIN_REUSED';

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle>
        {article.id ? 'Edit Article' : 'New Article'}
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}>
          Write for patients and families. Aim for approximately a sixth-grade reading level. Explain medical or nutrition terms in plain language.
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" onClose={onClearError} sx={{ mb: 2 }}>{error}</Alert>}

        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, color: 'text.secondary' }}>BASIC INFORMATION</Typography>
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ flex: 1, minWidth: 180 }}>
            <InputLabel>Category *</InputLabel>
            <Select value={article.category_id} label="Category *" onChange={e => set({ category_id: e.target.value })}>
              {categories.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Content origin</InputLabel>
            <Select value={article.content_origin} label="Content origin" onChange={e => set({ content_origin: e.target.value as ContentOrigin })}>
              <MenuItem value="FAMELIA_ORIGINAL">Famēlia Original</MenuItem>
              <MenuItem value="PUBLIC_DOMAIN_REUSED">Public Domain Reused</MenuItem>
              <MenuItem value="LICENSED">Licensed</MenuItem>
              <MenuItem value="EXTERNAL_LINK_ONLY">External Link Only</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Language</InputLabel>
            <Select value={article.language} label="Language" onChange={e => set({ language: e.target.value })}>
              <MenuItem value="en">English</MenuItem>
              <MenuItem value="es">Spanish</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <TextField fullWidth required size="small" label="Headline / Title" value={article.title}
          onChange={e => set({ title: e.target.value })} sx={{ mb: 2 }}
          placeholder="e.g. Understanding Sodium and Blood Pressure" />
        <TextField fullWidth size="small" multiline rows={2} label="Short description (shown on feed card)"
          value={article.short_description} onChange={e => set({ short_description: e.target.value })}
          sx={{ mb: 2 }} placeholder="2–3 sentences visible before tapping 'Show More'." />
        <TextField fullWidth size="small" multiline rows={10} label="Article body (Markdown supported)"
          value={article.body} onChange={e => set({ body: e.target.value })} sx={{ mb: 2 }}
          placeholder="Use ## for headings, **bold**, _italic_, - bullet lists." />

        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <TextField size="small" label="Reading time (min)" type="number" sx={{ width: 150 }}
            value={article.reading_time_minutes}
            onChange={e => set({ reading_time_minutes: e.target.value === '' ? '' : parseInt(e.target.value) })} />
          <TextField size="small" label="Hero icon (Ionicons)" sx={{ width: 180 }}
            value={article.hero_icon} onChange={e => set({ hero_icon: e.target.value })} placeholder="heart-outline" />
          <TextField size="small" label="Sort priority" type="number" sx={{ width: 130 }}
            value={article.sort_priority} onChange={e => set({ sort_priority: parseInt(e.target.value) || 0 })}
            helperText="Lower = higher" />
          <FormControlLabel control={<Switch checked={article.featured} onChange={e => set({ featured: e.target.checked })} color="warning" />} label="Featured ⭐" />
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
          <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ flexGrow: 1 }}>SOURCES & REFERENCES</Typography>
          <Button size="small" startIcon={<AddIcon />} onClick={() => setAddingSource(true)}>Add source</Button>
        </Box>

        {hasNCM && (
          <Alert severity="warning" icon={<InfoIcon />} sx={{ mb: 2, fontSize: 13 }}>
            <strong>NCM reminder:</strong> An NCM subscription does not provide a general right to republish NCM content in the Famēlia app.
          </Alert>
        )}

        {articleSources.map((s, idx) => {
          const src = resolveSrc(s.trusted_source_id) ?? s.trusted_source;
          const blocked = isBlocked(s.trusted_source_id);
          return (
            <Paper key={idx} variant="outlined" sx={{ p: 2, mb: 1.5, bgcolor: blocked ? '#FFF3E0' : '#FAFAFA' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="body2" fontWeight={700}>{src?.short_name ?? 'Unknown'}</Typography>
                {src && policyBadge(src.usage_policy)}
                {blocked && <Chip label="⚠ Blocked for reuse" size="small" color="error" variant="outlined" />}
                <Box sx={{ flexGrow: 1 }} />
                <FormControlLabel control={<Switch size="small" checked={s.is_primary} onChange={e => updateSource(idx, { is_primary: e.target.checked })} />} label="Primary" sx={{ mr: 0 }} />
                <IconButton size="small" color="error" onClick={() => removeSource(idx)}><DeleteIcon fontSize="small" /></IconButton>
              </Box>
              {blocked && <Alert severity="error" sx={{ mb: 1, fontSize: 12 }}>Reference/Link Only source — cannot use as Public Domain Reused. Write original content citing this source.</Alert>}
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                <TextField size="small" label="Page/article title" value={s.source_title} onChange={e => updateSource(idx, { source_title: e.target.value })} sx={{ flex: 2, minWidth: 200 }} />
                <TextField size="small" label="Source URL (https://)" value={s.source_url} onChange={e => updateSource(idx, { source_url: e.target.value })} sx={{ flex: 3, minWidth: 200 }} />
                <TextField size="small" label="Date" type="date" value={s.source_date} onChange={e => updateSource(idx, { source_date: e.target.value })} InputLabelProps={{ shrink: true }} sx={{ width: 150 }} />
                <TextField size="small" label="Accessed" type="date" value={s.accessed_at} onChange={e => updateSource(idx, { accessed_at: e.target.value })} InputLabelProps={{ shrink: true }} sx={{ width: 150 }} />
              </Box>
              {needsVerify(s) && (
                <FormControlLabel sx={{ mt: 1 }}
                  control={<Switch checked={s.rights_verified} color="success" onChange={e => updateSource(idx, { rights_verified: e.target.checked })} />}
                  label={<Typography variant="body2">I reviewed this specific source page and confirmed that the material Famēlia is reproducing is public domain and not marked as copyrighted or restricted.</Typography>} />
              )}
              {article.content_origin === 'LICENSED' && (
                <TextField fullWidth size="small" label="License / permission notes" value={s.license_notes}
                  onChange={e => updateSource(idx, { license_notes: e.target.value })}
                  placeholder="Describe the license or permission received." sx={{ mt: 1 }} />
              )}
            </Paper>
          );
        })}

        {addingSource && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#E3F2FD' }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Add source</Typography>
            <FormControl size="small" fullWidth sx={{ mb: 1.5 }}>
              <InputLabel>Trusted source *</InputLabel>
              <Select value={newSrc.trusted_source_id} label="Trusted source *"
                onChange={e => setNewSrc(p => ({ ...p, trusted_source_id: e.target.value }))}>
                {trustedSources.map(s => (
                  <MenuItem key={s.id} value={s.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <span>{s.short_name}</span>
                      <Box sx={{ flexGrow: 1 }} />
                      {policyBadge(s.usage_policy)}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {newSrc.trusted_source_id && resolveSrc(newSrc.trusted_source_id)?.usage_policy === 'REFERENCE_LINK_ONLY' && (
              <Alert severity="info" sx={{ mb: 1, fontSize: 12 }}>
                <strong>Reference/Link Only.</strong> Use as a reference. Do not paste or closely reproduce its text.
              </Alert>
            )}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="contained" size="small" onClick={addSource} disabled={!newSrc.trusted_source_id} sx={{ bgcolor: '#1565C0' }}>Add</Button>
              <Button size="small" onClick={() => setAddingSource(false)}>Cancel</Button>
            </Box>
          </Paper>
        )}

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, color: 'text.secondary' }}>REGISTERED DIETITIAN REVIEW</Typography>
        <Alert severity="info" sx={{ mb: 2, fontSize: 12 }}>
          Only toggle on when a Famēlia RD has actually reviewed this content. Do not mark articles as dietitian-reviewed merely by publishing.
        </Alert>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <FormControlLabel control={<Switch checked={article.reviewed_by_dietitian} color="success" onChange={e => set({ reviewed_by_dietitian: e.target.checked })} />} label="Reviewed by Famēlia RD" />
          {article.reviewed_by_dietitian && (
            <>
              <TextField size="small" label="Reviewer name" value={article.reviewer_name} onChange={e => set({ reviewer_name: e.target.value })} sx={{ width: 200 }} placeholder="Laura Russell" />
              <TextField size="small" label="Credentials" value={article.reviewer_credentials} onChange={e => set({ reviewer_credentials: e.target.value })} sx={{ width: 180 }} placeholder="MA, RDN, LD, CDCES" />
              <TextField size="small" label="Review date" type="date" value={article.reviewed_at} onChange={e => set({ reviewed_at: e.target.value })} InputLabelProps={{ shrink: true }} sx={{ width: 160 }} />
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={() => onSave('DRAFT')} disabled={saving} variant="outlined">
          {saving ? <CircularProgress size={18} /> : 'Save as Draft'}
        </Button>
        <Button onClick={() => onSave('PUBLISHED')} disabled={saving} variant="contained" sx={{ bgcolor: '#1565C0' }} startIcon={<PublishIcon />}>
          {saving ? <CircularProgress size={18} color="inherit" /> : article.status === 'PUBLISHED' ? 'Update & Publish' : 'Publish'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Article Preview ───────────────────────────────────────────────────────────
function ArticlePreview({ article }: { article: Article }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 700 }}>
        {(article as any).category?.name ?? 'Education'}
      </Typography>
      <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5, mb: 1 }}>{article.title}</Typography>
      {article.short_description && <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>{article.short_description}</Typography>}
      {article.reviewed_by_dietitian && article.reviewer_name && (
        <Box sx={{ bgcolor: '#E8F5E9', borderRadius: 2, px: 2, py: 1, mb: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
          <VerifiedIcon fontSize="small" color="success" />
          <Typography variant="body2" color="success.dark">
            Reviewed by {article.reviewer_name}{article.reviewer_credentials ? `, ${article.reviewer_credentials}` : ''}
            {article.reviewed_at ? ` · ${new Date(article.reviewed_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}` : ''}
          </Typography>
        </Box>
      )}
      <Divider sx={{ mb: 2 }} />
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>{article.body || '(No body content yet)'}</Typography>
      <Divider sx={{ mt: 3, mb: 1.5 }} />
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.6 }}>
        This information is for education only. It does not replace medical care or advice from your healthcare professional. Your Famēlia dietitian can help you understand how this information applies to you.
      </Typography>
    </Box>
  );
}

// ── Categories section ────────────────────────────────────────────────────────
function CategoriesSection() {
  const authHeader = useMemo(() => ({ Authorization: adminApi.getAuthToken() }), []);
  const [cats, setCats] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Category | null>(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await axios.get(`${EDU_API}/categories`, { headers: authHeader }); setCats(data.categories ?? []); }
    catch {} finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const BLANK_CAT: Category = { id: '', name: '', slug: '', icon: '', sort_order: cats.length, active: true };

  const handleSave = async () => {
    if (!editing || !editing.name.trim()) return;
    setSaving(true);
    try {
      if (editing.id) { await axios.put(`${EDU_API}/categories/${editing.id}`, editing, { headers: authHeader }); }
      else { await axios.post(`${EDU_API}/categories`, editing, { headers: authHeader }); }
      setEditing(null); load();
    } catch {}
    setSaving(false);
  };

  const toggleActive = async (cat: Category, active: boolean) => {
    await axios.put(`${EDU_API}/categories/${cat.id}`, { ...cat, active }, { headers: authHeader });
    load();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditing({ ...BLANK_CAT })} sx={{ bgcolor: '#1565C0' }}>Add Category</Button>
      </Box>
      {loading ? <CircularProgress /> : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead sx={{ bgcolor: '#F5F5F5' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Order</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Slug</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Active</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {cats.map(c => (
                <TableRow key={c.id} hover>
                  <TableCell>{c.sort_order}</TableCell>
                  <TableCell>{c.icon && <span style={{ marginRight: 6 }}>{c.icon}</span>}{c.name}</TableCell>
                  <TableCell><Typography variant="caption" color="text.secondary">{c.slug}</Typography></TableCell>
                  <TableCell><Switch size="small" checked={c.active} color="success" onChange={e => toggleActive(c, e.target.checked)} /></TableCell>
                  <TableCell align="right"><IconButton size="small" onClick={() => setEditing({ ...c })}><EditIcon fontSize="small" /></IconButton></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Dialog open={!!editing} onClose={() => setEditing(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{editing?.id ? 'Edit Category' : 'New Category'}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 0.5 }}>
            <TextField size="small" fullWidth label="Name *" value={editing?.name ?? ''} onChange={e => setEditing(p => p ? { ...p, name: e.target.value } : p)} />
            <TextField size="small" fullWidth label="Slug (auto-generated)" value={editing?.slug ?? ''} onChange={e => setEditing(p => p ? { ...p, slug: e.target.value } : p)} />
            <TextField size="small" fullWidth label="Icon (Ionicons name)" value={editing?.icon ?? ''} onChange={e => setEditing(p => p ? { ...p, icon: e.target.value } : p)} placeholder="heart-outline" />
            <TextField size="small" fullWidth label="Sort order" type="number" value={editing?.sort_order ?? 0} onChange={e => setEditing(p => p ? { ...p, sort_order: parseInt(e.target.value) || 0 } : p)} />
            <FormControlLabel control={<Switch checked={editing?.active ?? true} onChange={e => setEditing(p => p ? { ...p, active: e.target.checked } : p)} />} label="Active" />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || !editing?.name.trim()} sx={{ bgcolor: '#1565C0' }}>
            {saving ? <CircularProgress size={18} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── Trusted Sources section ───────────────────────────────────────────────────
interface RssArticle {
  title: string;
  description: string;
  url: string;
  published_at: string;
  image_url: string;
}

function TrustedSourcesSection() {
  const authHeader = useMemo(() => ({ Authorization: adminApi.getAuthToken() }), []);
  const [srcs, setSrcs] = useState<TrustedSource[]>([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [newSrc, setNewSrc] = useState({ name: '', short_name: '', notes: '' });
  const [saving, setSaving] = useState(false);

  // Browse state
  const [browsingSource, setBrowsingSource] = useState<TrustedSource | null>(null);
  const [rssArticles, setRssArticles] = useState<RssArticle[]>([]);
  const [rssLoading, setRssLoading] = useState(false);
  const [rssError, setRssError] = useState<string | null>(null);
  const [rssMessage, setRssMessage] = useState<string | null>(null);

  // Import pre-fill callback — lifted to Articles section via a shared signal
  const [pendingImport, setPendingImport] = useState<{ url: string; source: TrustedSource; title: string; description: string } | null>(null);

  const load = async () => {
    setLoading(true);
    try { const { data } = await axios.get(`${EDU_API}/trusted-sources`, { headers: authHeader }); setSrcs(data.sources ?? []); }
    catch {} finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const handleBrowse = async (src: TrustedSource) => {
    setBrowsingSource(src);
    setRssArticles([]);
    setRssError(null);
    setRssMessage(null);
    setRssLoading(true);
    try {
      const { data } = await axios.get(`${EDU_API}/source-articles`, {
        headers: authHeader,
        params: { source_id: src.id, limit: 10 },
        timeout: 20000,
      });
      if (data.message) setRssMessage(data.message);
      setRssArticles(data.articles ?? []);
    } catch (e: any) {
      setRssError(e.response?.data?.error ?? e.message ?? 'Could not load articles from this source.');
    }
    setRssLoading(false);
  };

  const handleAdd = async () => {
    setSaving(true);
    try { await axios.post(`${EDU_API}/trusted-sources`, newSrc, { headers: authHeader }); setAddOpen(false); setNewSrc({ name: '', short_name: '', notes: '' }); load(); }
    catch {} finally { setSaving(false); }
  };

  const toggleActive = async (src: TrustedSource, active: boolean) => {
    await axios.patch(`${EDU_API}/trusted-sources/${src.id}`, { active }, { headers: authHeader });
    load();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Click <strong>Browse articles</strong> on any source to see their latest content and add articles to the education feed.
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setAddOpen(true)} sx={{ ml: 2 }}>Add Custom Source</Button>
      </Box>

      {loading ? <CircularProgress /> : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead sx={{ bgcolor: '#F5F5F5' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Source</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Usage Policy</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Active</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Browse</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {srcs.map(s => (
                <TableRow key={s.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{s.name}</Typography>
                    {s.short_name !== s.name && <Typography variant="caption" color="text.secondary">{s.short_name}</Typography>}
                    {s.is_custom && <Chip label="Custom" size="small" sx={{ ml: 1, fontSize: 10 }} />}
                  </TableCell>
                  <TableCell>{policyBadge(s.usage_policy)}</TableCell>
                  <TableCell><Switch size="small" checked={s.active} color="success" onChange={e => toggleActive(s, e.target.checked)} /></TableCell>
                  <TableCell align="right">
                    <Tooltip title={`Browse recent articles from ${s.short_name}`}>
                      <Button
                        size="small" variant="outlined" startIcon={<RssFeedIcon />}
                        onClick={() => handleBrowse(s)}
                        sx={{ textTransform: 'none', borderColor: '#1565C0', color: '#1565C0' }}
                      >
                        Browse articles
                      </Button>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ── Browse articles dialog ── */}
      <Dialog open={!!browsingSource} onClose={() => setBrowsingSource(null)} maxWidth="md" fullWidth scroll="paper">
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <RssFeedIcon color="primary" />
            <span>Recent articles from {browsingSource?.name}</span>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Tap any article to open the Add Article form pre-filled with its headline and description. You can edit before saving.
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {browsingSource && policyBadge(browsingSource.usage_policy) && (
            <Box sx={{ px: 3, pt: 2, pb: 1 }}>
              <Alert
                severity={browsingSource.usage_policy === 'REFERENCE_LINK_ONLY' ? 'info' : 'success'}
                sx={{ fontSize: 12 }}
              >
                {browsingSource.usage_policy === 'REFERENCE_LINK_ONLY'
                  ? <>These articles are <strong>Reference / Link Only</strong>. When you add one, it will open in the user's browser — don't copy the article text.</>
                  : browsingSource.usage_policy === 'PUBLIC_DOMAIN_VERIFY_PAGE'
                  ? <>Some content from this source may be reusable. Verify the specific page's rights before setting content origin to "Public Domain Reused".</>
                  : <>Content from this source may be reusable — verify rights for each article individually.</>}
              </Alert>
            </Box>
          )}

          {rssLoading && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6, gap: 2 }}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary">Fetching latest articles…</Typography>
            </Box>
          )}

          {rssError && (
            <Box sx={{ p: 3 }}>
              <Alert severity="error">{rssError}</Alert>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                You can still add articles from this source manually using <strong>Import from URL</strong> in the Articles tab.
              </Typography>
            </Box>
          )}

          {rssMessage && !rssLoading && (
            <Box sx={{ p: 3 }}>
              <Alert severity="warning">{rssMessage}</Alert>
            </Box>
          )}

          {!rssLoading && !rssError && rssArticles.length === 0 && !rssMessage && (
            <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
              <Typography>No articles found in the RSS feed.</Typography>
            </Box>
          )}

          {!rssLoading && rssArticles.length > 0 && (
            <Box>
              {rssArticles.map((art, i) => (
                <Box
                  key={i}
                  sx={{
                    display: 'flex', gap: 2, px: 3, py: 2,
                    borderBottom: '1px solid #F0F0F0',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: '#F0F4FF' },
                    transition: 'background 0.15s',
                  }}
                  onClick={() => {
                    setBrowsingSource(null);
                    // Signal ArticlesSection to open import dialog with this URL
                    setPendingImport({
                      url: art.url,
                      source: browsingSource!,
                      title: art.title,
                      description: art.description,
                    });
                  }}
                >
                  {art.image_url ? (
                    <Box
                      component="img" src={art.image_url} alt=""
                      sx={{ width: 80, height: 60, borderRadius: 1.5, objectFit: 'cover', flexShrink: 0 }}
                      onError={(e: any) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <Box sx={{ width: 80, height: 60, borderRadius: 1.5, bgcolor: '#E3F2FD', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <RssFeedIcon sx={{ color: '#1565C0', opacity: 0.4 }} />
                    </Box>
                  )}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5, lineHeight: 1.4 }}>
                      {art.title}
                    </Typography>
                    {art.description && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {art.description}
                      </Typography>
                    )}
                    <Typography variant="caption" sx={{ color: '#1565C0', display: 'block', mt: 0.5 }}>
                      {art.published_at ? new Date(art.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                      {' · '}Click to add to Education feed →
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBrowsingSource(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ── Pending import: pass signal up via a temporary storage trick ── */}
      {pendingImport && (
        <PendingImportHandler
          pending={pendingImport}
          categories={[]}
          trustedSources={srcs}
          authHeader={authHeader}
          onDone={() => setPendingImport(null)}
        />
      )}

      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add Custom Trusted Source</DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 2, fontSize: 12 }}>Custom sources default to <strong>Reference / Link Only</strong>.</Alert>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField size="small" fullWidth label="Organization name *" value={newSrc.name} onChange={e => setNewSrc(p => ({ ...p, name: e.target.value }))} />
            <TextField size="small" fullWidth label="Short name (shown in app)" value={newSrc.short_name} onChange={e => setNewSrc(p => ({ ...p, short_name: e.target.value }))} />
            <TextField size="small" fullWidth multiline rows={2} label="Internal notes" value={newSrc.notes} onChange={e => setNewSrc(p => ({ ...p, notes: e.target.value }))} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAdd} disabled={saving || !newSrc.name.trim()}>
            {saving ? <CircularProgress size={18} /> : 'Add Source'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── Pending import handler: opens the full article editor pre-filled from a Browse click ──
function PendingImportHandler({
  pending, categories, trustedSources, authHeader, onDone,
}: {
  pending: { url: string; source: TrustedSource; title: string; description: string };
  categories: Category[];
  trustedSources: TrustedSource[];
  authHeader: { Authorization: string };
  onDone: () => void;
}) {
  const [cats, setCats] = useState<Category[]>(categories);
  const [editing, setEditing] = useState<Article>({
    ...BLANK_ARTICLE,
    title: pending.title,
    short_description: pending.description,
    content_origin: pending.source.usage_policy === 'REFERENCE_LINK_ONLY' ? 'EXTERNAL_LINK_ONLY' : 'EXTERNAL_LINK_ONLY',
  });
  const [editingSources] = useState<ArticleSource[]>([{
    ...BLANK_SOURCE,
    trusted_source_id: pending.source.id,
    source_url: pending.url,
    source_title: pending.title,
    accessed_at: new Date().toISOString().split('T')[0],
    is_primary: true,
    trusted_source: pending.source,
  }]);
  const [srcs, setSrcs] = useState<ArticleSource[]>(editingSources);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    axios.get(`${EDU_API}/categories`, { headers: authHeader }).then(r => setCats(r.data.categories ?? []));
  }, [authHeader]);

  const handleSave = async (targetStatus?: ArticleStatus) => {
    setSaving(true);
    try {
      const status = targetStatus ?? 'DRAFT';
      const payload = {
        ...editing, status,
        published_at: status === 'PUBLISHED' ? new Date().toISOString() : null,
        reading_time_minutes: editing.reading_time_minutes === '' ? null : editing.reading_time_minutes,
      };
      const { data: newArt } = await axios.post(`${EDU_API}/articles`, payload, { headers: authHeader });
      for (const s of srcs) {
        if (s.trusted_source_id) {
          await axios.post(`${EDU_API}/articles/${newArt.id}/sources`, {
            trusted_source_id: s.trusted_source_id,
            source_title: s.source_title || null,
            source_url: s.source_url || null,
            accessed_at: s.accessed_at || null,
            is_primary: true,
          }, { headers: authHeader });
        }
      }
      onDone();
    } catch (e: any) {
      setError(e.response?.data?.error ?? e.message);
    }
    setSaving(false);
  };

  return (
    <ArticleEditDialog
      article={editing}
      articleSources={srcs}
      categories={cats}
      trustedSources={trustedSources}
      saving={saving}
      onChangeArticle={setEditing}
      onChangeSources={setSrcs}
      onSave={handleSave}
      onClose={onDone}
      error={error}
      onClearError={() => setError(null)}
    />
  );
}

// ── User Submissions section ──────────────────────────────────────────────────
type SubStatus = 'PENDING' | 'APPROVED' | 'REJECTED';
interface UserSubmission {
  id: string;
  title: string;
  short_description?: string;
  body?: string;
  external_url?: string;
  submission_type: 'link' | 'full_article';
  status: SubStatus;
  admin_notes?: string;
  created_at: string;
  category?: { id: string; name: string; slug: string } | null;
  profile?: { id: string; first_name: string; last_name: string } | null;
}

const SUB_STATUS_COLORS: Record<SubStatus, 'warning' | 'success' | 'error'> = {
  PENDING: 'warning', APPROVED: 'success', REJECTED: 'error',
};

function UserSubmissionsSection() {
  const [subs, setSubs] = useState<UserSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<SubStatus | 'ALL'>('PENDING');
  const [saving, setSaving] = useState<string | null>(null); // id of row being actioned
  const [adminNotesOpen, setAdminNotesOpen] = useState<UserSubmission | null>(null);
  const [adminNotesDraft, setAdminNotesDraft] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${EDU_API}/submissions`);
      setSubs(data.submissions ?? []);
    } catch (e: any) {
      setError(e?.response?.data?.error ?? e.message ?? 'Failed to load submissions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const action = async (id: string, status: SubStatus, admin_notes?: string) => {
    setSaving(id);
    try {
      await axios.patch(`${EDU_API}/submissions/${id}`, { status, admin_notes });
      setSubs(prev => prev.map(s => s.id === id ? { ...s, status, admin_notes: admin_notes ?? s.admin_notes } : s));
    } catch (e: any) {
      alert(e?.response?.data?.error ?? 'Action failed');
    } finally {
      setSaving(null);
      setAdminNotesOpen(null);
    }
  };

  const visible = filterStatus === 'ALL' ? subs : subs.filter(s => s.status === filterStatus);

  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;
  if (error) return <Box sx={{ p: 4 }}><Typography color="error">{error}</Typography></Box>;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6" fontWeight={700}>User-submitted Articles</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {(['ALL', 'PENDING', 'APPROVED', 'REJECTED'] as const).map(s => (
            <Chip
              key={s}
              label={`${s === 'ALL' ? `All (${subs.length})` : `${s} (${subs.filter(x => x.status === s).length})`}`}
              color={s === 'ALL' ? 'default' : SUB_STATUS_COLORS[s as SubStatus]}
              variant={filterStatus === s ? 'filled' : 'outlined'}
              size="small"
              onClick={() => setFilterStatus(s)}
              sx={{ cursor: 'pointer' }}
            />
          ))}
        </Box>
      </Box>

      {visible.length === 0 ? (
        <Typography color="text.secondary" sx={{ mt: 4, textAlign: 'center' }}>
          No {filterStatus === 'ALL' ? '' : filterStatus.toLowerCase()} submissions.
        </Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Submitted by</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.map(sub => (
                <TableRow key={sub.id} hover>
                  <TableCell>
                    <Typography variant="body2">
                      {sub.profile ? `${sub.profile.first_name} ${sub.profile.last_name}` : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600} sx={{ maxWidth: 260, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {sub.title}
                    </Typography>
                    {sub.external_url && (
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', maxWidth: 260 }}>
                        🔗 {sub.external_url}
                      </Typography>
                    )}
                    {sub.short_description && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {sub.short_description}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip label={sub.submission_type === 'link' ? '🔗 Link' : '✍️ Article'} size="small" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{sub.category?.name ?? '—'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={sub.status} size="small" color={SUB_STATUS_COLORS[sub.status]} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">{new Date(sub.created_at).toLocaleDateString()}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                      {sub.external_url && (
                        <Tooltip title="Open link">
                          <IconButton size="small" onClick={() => window.open(sub.external_url, '_blank')}>
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {sub.status !== 'APPROVED' && (
                        <Tooltip title="Approve (creates a DRAFT article)">
                          <IconButton
                            size="small" color="success"
                            disabled={saving === sub.id}
                            onClick={() => action(sub.id, 'APPROVED')}
                          >
                            {saving === sub.id ? <CircularProgress size={14} /> : <PublishIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                      )}
                      {sub.status !== 'REJECTED' && (
                        <Tooltip title="Reject">
                          <IconButton
                            size="small" color="error"
                            disabled={saving === sub.id}
                            onClick={() => { setAdminNotesOpen(sub); setAdminNotesDraft(sub.admin_notes ?? ''); }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Reject + notes dialog */}
      <Dialog open={!!adminNotesOpen} onClose={() => setAdminNotesOpen(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Submission</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Optionally add a note for your records (not shown to the user yet):
          </Typography>
          <TextField
            label="Admin notes (optional)"
            value={adminNotesDraft}
            onChange={e => setAdminNotesDraft(e.target.value)}
            multiline rows={3} fullWidth size="small"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdminNotesOpen(null)}>Cancel</Button>
          <Button
            color="error" variant="contained"
            disabled={!!saving}
            onClick={() => adminNotesOpen && action(adminNotesOpen.id, 'REJECTED', adminNotesDraft)}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
